// web_components/wcProduct/wcProduct.ts

import { environmentVisibilitySettings, productTypeVisibilitySettings } from './wcProduct_Settings';
import { WebComponent_Base } from '../WebComponent_Base';
import { WebComponent_Utils } from '../WebComponent_Utils';
import {
	attachSelectClickListener,
	attachSelectListItemListeners,
	handleFormSubmission,
	validateQuantityInput,
} from './wcProduct_Utils';
import { renderProductHTML } from './wcProduct_Renderer';
import { NotificationService, NotificationType } from '../../utils/NotificationService';

/**
 * A custom web component for displaying a product.
 * Displays product information based on settings from settings files and data attributes.
 */
export class WcProduct extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcProduct';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		// Variables
		const variables = this.getAttribute('data-variables');
		if (variables) {
			try {
				this.dataAttributes['variables'] = JSON.parse(variables);
			} catch (e) {
				console.error('wcProduct Component: Invalid JSON in data-variables attribute:', e);
				this.dataAttributes['variables'] = null;
			}
		}

		// Product Type
		const productType = this.getAttribute('data-product-type');

		if (productType && productType.trim() !== '') {
			// If productType is not null and not empty, assign it
			this.dataAttributes['product_type_name'] = productType;
		} else {
			// If productType is null or empty, issue a warning and set to default
			console.warn('wcProduct Component: Missing or empty data-product-type attribute.');
			this.dataAttributes['product_type_name'] = 'default';
		}

		// data-website
		const website = this.getAttribute('data-website');

		if (website && website.trim() !== '') {
			// If website is not null and not empty, assign it
			this.dataAttributes['website'] = website;
		} else {
			// If website is null or empty, issue a warning and set to default
			console.warn('wcProduct Component: Missing or empty data-website attribute.');
			this.dataAttributes['website'] = 'default';
		}
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		const website = this.dataset.website || 'default';
		const productType = this.dataset.productType || 'default';
		const environmentSettings =
			environmentVisibilitySettings[website] || environmentVisibilitySettings['default'];
		const productSettings =
			productTypeVisibilitySettings[productType] || productTypeVisibilitySettings['default'];
		const settings = WebComponent_Utils.mergeSettings(productSettings, environmentSettings);

		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot, settings);
	}

	/**
	 * Attaches listeners for validating the quantity input field, managing the custom select component,
	 * and handling the form submission process.
	 *
	 * =======================
	 * QUANTITY INPUT LISTENERS
	 * =======================
	 * For the quantity input field:
	 * - Validates the input on blur to ensure the quantity is between 1 and 99.
	 * - Restricts input to whole numbers in real-time and provides accessible feedback if the value is out of range.
	 *
	 * =========================
	 * CUSTOM SELECT LISTENERS
	 * =========================
	 * For the custom select component:
	 * - Attaches click event listeners to the select button and each list item.
	 * - Toggles the visibility of the options list and updates the `aria-expanded` attribute for accessibility.
	 * - Applies a focus trap when the options list is opened and clears it when closed.
	 *
	 * ===========================
	 * FORM SUBMISSION LISTENER
	 * ===========================
	 * For the form submission:
	 * - Prevents the default form submission behavior.
	 * - Retrieves the input values for the product type, quantity, and selected option.
	 * - Calls the handleFormSubmission method to process the form data (e.g., adding to the cart or updating the UI).
	 *
	 * @private
	 * @returns {void} This method does not return a value.
	 */
	protected attachListeners(): void {
		const form = this.shadowRoot!.querySelector('.wc-product__atb-form');
		const productIdInput = this.shadowRoot!.querySelector(
			'.wc-product__product-id',
		) as HTMLInputElement;
		const productTypeInput = this.shadowRoot!.querySelector(
			'.wc-product__product-type',
		) as HTMLInputElement;
		const productDepositInput = this.shadowRoot!.querySelector(
			'.wc-product__product-deposit',
		) as HTMLInputElement;
		const quantityInput = this.shadowRoot!.querySelector('.wc-product__atb-qty');
		const selectButton = this.shadowRoot!.querySelector('.wc-product__atb-select-btn');

		// Trigger showSliderBufferAndButtons function as component builds + on window resize.
		if (document.readyState === 'loading') {
			this.showSliderBufferAndButtons();
		} else {
			this.showSliderBufferAndButtons();
		}

		// ================================
		// FORM SUBMISSION LISTENER
		// ================================
		if (form) {
			form.addEventListener('submit', (event: Event) => {
				event.preventDefault(); // Prevent the default form submission behavior
				const productId =
					productIdInput instanceof HTMLInputElement
						? parseInt(productIdInput.value)
						: NaN;

				if (!Number.isInteger(productId)) {
					NotificationService.showNotification(
						NotificationType.error,
						'Add to Basket Failed',
						'Sorry, we could not add this item to your basket',
					);
					return;
				}

				// Retrieve the input values
				// Ensure that productType is a string by checking if productTypeInput is an input element
				const productType =
					productTypeInput instanceof HTMLInputElement
						? productTypeInput.value
						: 'default';
				const quantity = parseInt(
					quantityInput ? (quantityInput as HTMLInputElement).value : '1',
				);
				const selectedOption = selectButton?.getAttribute('data-selected-value') || '';
				const isPersonalised = false;
				const depositPrice = productDepositInput ? productDepositInput.value : '';

				// Custom form handling logic, like adding to cart or updating UI
				handleFormSubmission(
					this,
					productId,
					productType,
					quantity,
					selectedOption,
					isPersonalised,
					depositPrice,
				);
			});
		}

		// ================================
		// QUANTITY INPUT LISTENERS
		// ================================
		const inputElement = this.shadowRoot!.querySelector(
			'.wc-product__atb-qty',
		) as HTMLInputElement;
		const errorElement = this.shadowRoot!.querySelector(
			'.wc-product__atb-qty-error',
		) as HTMLElement;

		if (inputElement && errorElement) {
			// On blur (when the user moves focus away), validate the input
			inputElement.addEventListener('blur', () =>
				validateQuantityInput(inputElement, errorElement),
			);

			// Real-time input restriction to whole numbers and values between 1 and 99
			inputElement.addEventListener('input', () => {
				let value = inputElement.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
				inputElement.value = value;

				// Convert the value to an integer for further checks
				const intValue = parseInt(value, 10);

				// Ensure the value is between 1 and 99 and provide live feedback
				if (intValue > 99) {
					inputElement.value = '99';
					errorElement.textContent = 'Quantity cannot exceed 99.'; // Accessible feedback for screen readers
					inputElement.setAttribute('aria-invalid', 'true');
				} else if (intValue < 1 && inputElement.value !== '') {
					inputElement.value = '1';
					errorElement.textContent = 'Quantity cannot be less than 1.'; // Accessible feedback for screen readers
					inputElement.setAttribute('aria-invalid', 'true');
				} else {
					errorElement.textContent = ''; // Clear errors if valid
					inputElement.setAttribute('aria-invalid', 'false');
				}
			});
		}

		// ================================
		// CUSTOM SELECT LISTENERS
		// ================================
		const selectBtn = this.shadowRoot!.querySelector(
			'.wc-product__atb-select-btn',
		) as HTMLElement;
		const selectListItems = this.shadowRoot!.querySelectorAll(
			'.wc-product__atb-select-option',
		) as NodeListOf<HTMLElement>;
		const optionsList = this.shadowRoot!.querySelector(
			'.wc-product__atb-select-options',
		) as HTMLElement;

		if (!selectBtn || !selectListItems || !optionsList) return;

		// Attach click event listeners
		attachSelectClickListener(selectBtn, optionsList);
		attachSelectListItemListeners(selectListItems, selectBtn, optionsList);

		// GTM Click Events
		WebComponent_Utils.addGTMClickListener(
			this.shadowRoot!,
			'.wc-product__image-link',
			'wc-product',
			'image',
			false,
		);

		WebComponent_Utils.addGTMClickListener(
			this.shadowRoot!,
			'.wc-product__review-count-total',
			'wc-product',
			'review count',
		);

		WebComponent_Utils.addGTMClickListener(
			this.shadowRoot!,
			'.wc-product__more-link',
			'wc-product',
			'read more',
		);
	}

	/**
	 * Displays the track buffer pseudo-element on the case contents slider
	 * if the total width of the bottles exceeds the visible container width.
	 */
	showSliderBufferAndButtons(): void {
		const shadowRoot = this?.shadowRoot;
		if (!shadowRoot) return;

		const productListing = document.querySelectorAll<HTMLElement>(
			'.wc-product__listing-slider',
		);

		if (!productListing) return;

		productListing.forEach((el) => {
			const productsSliderTrack = el?.querySelector<HTMLElement>('.slider__track');
			const products = productsSliderTrack?.querySelectorAll('wc-product');

			if (!productsSliderTrack || !products?.length) return;

			const productListingWidth = el.clientWidth;
			const totalProductsWidth = 320 * products.length; // 300px bottle + 20px gap

			// '+ 4' is a slight buffer to account for rounding/pixel calculation discrepancies
			const shouldShowBufferAndButtons = totalProductsWidth > productListingWidth + 4;
			productsSliderTrack.classList.toggle('show-buffer-buttons', shouldShowBufferAndButtons);
		});
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-product', WcProduct);
